/**
 * Folgen eines gescheiterten oder gekippten Haushalts.
 *
 * Vorläufige Haushaltsführung (Bundestag lehnt ab): Es gelten die alten Etats, neue Ausgaben-Gesetze
 * sind gesperrt, Wachstum, Zustimmung und Koalitionsklima leiden jeden Monat. Die Regierung kann
 * jederzeit einen neuen Entwurf einbringen. Hält der Zustand zu lange an, zerbricht die Koalition.
 *
 * Haushaltssperre (Karlsruhe kippt den Haushalt): Die Regierung bekommt ein Sparziel und eine Frist.
 * Sie schließt die Lücke selbst: Gesetze aufheben, im Nachtragshaushalt Etats kürzen oder Einnahmen
 * erhöhen. Gelingt das nicht, kürzt das Finanzministerium pauschal, mit harten politischen Folgen.
 */
import { OUTLETS } from '../content/parties';
import { RESS } from '../content/ressorts';
import { debtLimit, structSaldo, openBudget } from './budget';
import { toast } from './govern';
import { press } from './media';
import { S, log } from './state';
import { clamp, dateLabel, de } from './util';

export interface Fiscal { mode: 'provisional' | 'sperre'; year: number; since: number; until?: number; need0?: number; warned?: boolean }

export const SPERRE_MONTHS = 8;
export const PROVISIONAL_WARN = 4, PROVISIONAL_BREAK = 7;

/** Lücke zur Schuldenbremse in Mrd €/Jahr (positiv = es fehlt Geld). */
export const fiscalGap = (): number => Math.max(0, debtLimit() - structSaldo());

/** Ausgabenstopp: neue Gesetze mit Kosten sind gesperrt. Gibt den Grund zurück oder null. */
export function spendingFreeze(fx: { b?: number } | null | undefined): string | null {
  const F = S.fiscal; if (!F || !fx || (fx.b ?? 0) >= -0.3) return null;
  return F.mode === 'provisional'
    ? 'Vorläufige Haushaltsführung: Neue Ausgaben sind erst wieder möglich, wenn ein Haushalt beschlossen ist.'
    : 'Haushaltssperre: Solange das Sparziel nicht erreicht ist, sind neue Ausgaben gesperrt.';
}

/** Bundestag hat den Haushalt abgelehnt. */
export function startProvisional(year: number) {
  if (S.fiscal && S.fiscal.mode === 'sperre') return; // die Sperre ist strenger und bleibt
  S.fiscal = { mode: 'provisional', year, since: S.m };
  log(`Vorläufige Haushaltsführung: Bis ein neuer Haushalt beschlossen ist, gelten die alten Etats und neue Ausgaben sind gesperrt. Bringt so schnell wie möglich einen neuen Entwurf ein.`, 'bad');
}

/** Karlsruhe hat den Haushalt gekippt. */
export function startSperre(year: number) {
  const need = fiscalGap();
  if (need < 0.5) { log(`Das Urteil zum Haushalt ${year} hat kaum Folgen, die Schuldenbremse wird inzwischen eingehalten.`); return; }
  S.fiscal = { mode: 'sperre', year, since: S.m, until: S.m + SPERRE_MONTHS, need0: Math.round(need * 10) / 10 };
  S.coal = clamp(S.coal - 6, 0, 100); S.basis -= 1;
  press(OUTLETS[0].n, `Karlsruhe kippt den Haushalt ${year}: Regierung muss ${de(need)} Milliarden einsparen`, -2.5);
  log(`Das Bundesverfassungsgericht erklärt den Haushalt ${year} für verfassungswidrig. Haushaltssperre: Bis ${dateLabel(S.m + SPERRE_MONTHS)} müssen ${de(need)} Mrd € im Jahr eingespart werden, durch Aufhebung von Gesetzen, Kürzungen im Nachtragshaushalt oder höhere Einnahmen. Sonst kürzt das Finanzministerium pauschal.`, 'bad');
  toast('Karlsruhe kippt den Haushalt: Haushaltssperre', 'bad');
}

/** Ein Haushalt wurde beschlossen. */
export function onBudgetPassed() {
  const F = S.fiscal; if (!F) return;
  if (F.mode === 'provisional') { S.fiscal = null; log('Mit dem neuen Haushalt endet die vorläufige Haushaltsführung.', 'good'); }
}

/** Pauschale Zwangskürzung über alle Ressorts, wenn die Frist verstreicht. */
function forcedCuts(need: number) {
  const room = RESS.reduce((s: number, r: any) => s + ((S.etat[r.id] || 0) - r.min), 0);
  if (room > 0) { const t = Math.min(need, room); RESS.forEach((r: any) => { const x = S.etat[r.id] || 0; S.etat[r.id] = Math.round((x - (x - r.min) * t / room) * 10) / 10; }); need -= t; }
  if (need > 0) S.ein = Math.round(((S.ein || 0) + need) * 10) / 10; // Rest über Abgaben
  S.saldo = structSaldo();
}

/** Monatlich: Folgen anwenden, Ziel prüfen, Fristen durchsetzen. Gibt true zurück, wenn etwas passiert ist. */
export function fiscalTick(): boolean {
  const F = S.fiscal; if (!F) return false;
  const months = S.m - F.since;
  if (F.mode === 'provisional') {
    S.basis -= .15; S.coal = clamp(S.coal - 1, 0, 100); S.shockGr -= .03;
    if (months >= PROVISIONAL_WARN && !F.warned) {
      F.warned = true; S.coal = clamp(S.coal - 8, 0, 100);
      press(OUTLETS[0].n, `Seit ${months} Monaten ohne Haushalt: Koalition unter Druck`, -1.2);
      log(`Seit ${months} Monaten gibt es keinen beschlossenen Haushalt. Ohne neuen Etat bis ${dateLabel(F.since + PROVISIONAL_BREAK)} droht das Ende der Regierung.`, 'bad');
      return true;
    }
    if (months >= PROVISIONAL_BREAK && !(S.budget && S.budget.stage === 'bt')) {
      if (S.partners.length) { S.end = 'coal'; S.phase = 'end'; log('Ohne Haushalt zerbricht die Koalition. Es gibt Neuwahlen.', 'bad'); }
      else { S.basis -= 2; F.since = S.m; F.warned = false; log('Die Regierung ist ohne Haushalt handlungsunfähig. Die Zustimmung bricht ein.', 'bad'); }
      press(OUTLETS[0].n, 'Regierung scheitert am Haushalt', -2);
      return true;
    }
    return false;
  }
  // Haushaltssperre
  S.basis -= .1; S.shockGr -= .02;
  const gap = fiscalGap();
  if (gap <= 0.05) {
    S.fiscal = null; S.basis += .6;
    press(OUTLETS[1].n, 'Haushaltssperre aufgehoben: Regierung erreicht ihr Sparziel', 1);
    log(`Das Sparziel ist erreicht. Die Haushaltssperre ist aufgehoben.`, 'good'); toast('Haushaltssperre aufgehoben', 'good');
    return true;
  }
  if (F.until != null && S.m >= F.until) {
    forcedCuts(gap); S.fiscal = null;
    S.coal = clamp(S.coal - 12, 0, 100); S.basis -= 2; S.shockGr -= .2;
    press(OUTLETS[0].n, `Kahlschlag per Rasenmäher: Finanzministerium kürzt pauschal ${de(gap)} Milliarden`, -2.5);
    log(`Die Frist ist verstrichen. Das Finanzministerium kürzt alle Etats pauschal um insgesamt ${de(gap)} Mrd €. Ministerien und Koalitionspartner sind empört.`, 'bad');
    toast('Pauschale Zwangskürzung', 'bad');
    return true;
  }
  return false;
}

/** Neuen Haushaltsentwurf einbringen (vorläufige Haushaltsführung) oder Nachtragshaushalt (Sperre). */
export function budgetRetry() {
  if (!S.fiscal || S.budget || S.phase !== 'govern') return;
  openBudget();
  if (S.fiscal.mode === 'sperre') log('Das Kabinett stellt einen Nachtragshaushalt auf, um das Sparziel zu erreichen.');
}
